import type { BuyerFundingFlowSession } from "./types";
import { formatMoney } from "../../domain/money";

export type SecuredBankDetailsFields = {
  partyReceivingFunds: string;
  fileNumber: string;
  address: string;
  creditTo: string;
  furtherCreditTo: string;
  correspondentBank: string;
  beneficiaryBank: string;
  amountDue: string;
  paymentRail: string;
  memoRequired: string;
};

export function securedBankDetailsFields(session: BuyerFundingFlowSession): SecuredBankDetailsFields {
  const i = session.instructions;
  return {
    partyReceivingFunds: session.officeName,
    fileNumber: session.fileCode,
    address: session.propertyAddress,
    creditTo: i.creditTo || i.beneficiaryName,
    furtherCreditTo: i.furtherCreditTo || `${i.beneficiaryName} · Acct ending ${i.accountLast4}`,
    correspondentBank: i.correspondentBank || "Not applicable for this wire",
    beneficiaryBank: `${i.bankName} · Routing ending ${i.routingLast4}`,
    amountDue: formatMoney(session.amountDue),
    paymentRail: i.paymentRail,
    memoRequired: i.memoRequired
  };
}

export function securedBankDetailsFilename(fileCode: string): string {
  return `secured-bank-details-${fileCode.toUpperCase()}.html`;
}

export function buildSecuredBankDetailsHtml(session: BuyerFundingFlowSession): string {
  const f = securedBankDetailsFields(session);
  const rows = [
    ["Party receiving funds", f.partyReceivingFunds],
    ["File number", f.fileNumber],
    ["Address", f.address],
    ["Credit to", f.creditTo],
    ["Further credit to", f.furtherCreditTo],
    ["Correspondent bank", f.correspondentBank],
    ["Beneficiary bank", f.beneficiaryBank],
    ["Amount due", f.amountDue],
    ["Payment rail", f.paymentRail],
    ["Required memo / reference", f.memoRequired]
  ] as const;

  const rowHtml = rows
    .map(
      ([label, value]) =>
        `<tr><th scope="row">${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Secured bank details · ${escapeHtml(f.fileNumber)}</title>
  <style>
    body { font-family: Georgia, "Times New Roman", serif; max-width: 42rem; margin: 2rem auto; color: #0f1c2e; }
    .brand { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.5rem; }
    .shield { width: 2.5rem; height: 2.5rem; border-radius: 999px; background: #16a34a; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; }
    .tag { background: #dcfce7; color: #166534; font-size: 0.65rem; font-weight: 800; letter-spacing: 0.12em; padding: 0.25rem 0.5rem; border-radius: 4px; }
    h1 { font-size: 1.75rem; margin: 0.5rem 0 1.5rem; font-family: system-ui, sans-serif; }
    table { width: 100%; border-collapse: collapse; font-size: 0.95rem; }
    th { text-align: left; vertical-align: top; width: 42%; padding: 0.65rem 0.75rem 0.65rem 0; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.08em; color: #596158; font-family: system-ui, sans-serif; }
    td { padding: 0.65rem 0; border-bottom: 1px solid #e8e4dc; font-weight: 600; }
    .footer { margin-top: 2rem; background: #2563eb; color: white; text-align: center; padding: 1rem; font-family: system-ui, sans-serif; font-weight: 800; letter-spacing: 0.15em; font-size: 0.85rem; }
    .disclaimer { margin-top: 1rem; font-size: 0.8rem; color: #596158; font-family: system-ui, sans-serif; line-height: 1.5; }
  </style>
</head>
<body>
  <div class="brand">
    <span class="tag">VETO SECURE</span>
    <div class="shield" aria-hidden="true">✓</div>
  </div>
  <h1>Secured bank details</h1>
  <table>${rowHtml}</table>
  <div class="footer">OFFICE-PUBLISHED INSTRUCTIONS</div>
  <p class="disclaimer">
    This document records the wiring instructions your office published through Veto. Veto does not move money,
    verify funds, insure your transfer, or approve your payment. The office decides whether funds may be relied on.
    If you receive different wire details elsewhere, contact your escrow office before sending funds.
  </p>
  <p class="disclaimer"><small>Generated ${new Date().toISOString()} · Instruction version ${escapeHtml(session.instructions.versionLabel)}</small></p>
</body>
</html>`;
}

export function downloadSecuredBankDetails(session: BuyerFundingFlowSession): void {
  const html = buildSecuredBankDetailsHtml(session);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = securedBankDetailsFilename(session.fileCode);
  anchor.click();
  URL.revokeObjectURL(url);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
